import _ from 'lodash';
import config from '../../../../../utils/config';
import logError from '../../../../../utils/errorUtil';
import { documentKeys, inputVerificationTypes, overrideDefaults } from './data';
import { mergePdfs, openDocumentInNewTab } from '../../../../utils/common-utils';

const ORIGIN = config.origin;

export const getDataForDocuments = async (releaseNumber, selectedReleaseLineName, currentUser, releaseLineData) => {
    let dataToSend = {
        authentication: {
            userId: currentUser.authentication.userId,
            userEmail: currentUser.authentication.userEmail,
            userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
        },
        releaseNumber,
        selectedReleaseLineName,
        userName: currentUser?.authentication?.userName,
        releaseLineData,
    };

    const response = await fetch(`${ORIGIN}/wms/documents/get-release-document-info`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
    });

    if (!response.ok) throw new Error('Failed to get data for documents');

    const responseData = await response.json();

    return responseData;
};

export const printGeneratedDocuments = async (
    documents,
    releaseLineNumber,
    releaseNumber,
    currentUser,
    requirePackSheet,
    isWillCall
) => {
    let dataToSend = {
        authentication: {
            userId: currentUser.authentication.userId,
            userEmail: currentUser.authentication.userEmail,
            userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
        },
        documents,
        releaseLineNumber,
        releaseNumber,
    };
    try {
        const res = await fetch(`${ORIGIN}/wms/documents/get-documents`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataToSend),
        });

        const data = await res.json();
        if (!data) throw new Error('No PDF data returned');

        const allPdfPages = [];

        const groupedDocs = _.groupBy(data, (doc) => Object.keys(doc)[0]);

        for (const key in groupedDocs) {
            let count = 1;
            if (requirePackSheet && key === 'packingSheetPriced') count = 4;
            if (isWillCall && key === 'packingSheetUnpriced') count = 3;

            const docsToPrint = groupedDocs[key];

            const buffersForThisType = docsToPrint.map((doc) => {
                const bufferData = doc[key].data;
                return new Uint8Array(bufferData).buffer;
            });

            for (let i = 0; i < count; i++) {
                allPdfPages.push(...buffersForThisType);
            }
        }
        await mergePdfPagesAndOpen(allPdfPages);
    } catch (err) {
        logError(err, 'printGeneratedDocuments');
        throw err;
    }
};

export const mergePdfPagesAndOpen = async (pdfData) => {
    const mergedData = await mergePdfs(null, pdfData);

    openDocumentInNewTab(mergedData);
};

export const submitUpdatedHeaderData = async (currentUser, updatedPackingSheetData) => {
    try {
        let dataToSend = {
            updatedPackingSheetData,
            authentication: {
                userId: currentUser.authentication.userId,
                userEmail: currentUser.authentication.userEmail,
                userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
            },
        };

        await fetch(`${ORIGIN}/wms/trace/update-release-header`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(dataToSend),
        });
    } catch (error) {
        logError(error, 'printAllPickingLabels');
    }
};

export const updateBigAtaLinesInSF = async (currentUser, patches = []) => {
    if (!patches.length) return { ok: true };

    const dataToSend = {
        authentication: {
            userId: currentUser.authentication.userId,
            userEmail: currentUser.authentication.userEmail,
            userCompany: currentUser.authentication.userCompany || localStorage.getItem('company'),
        },
        patches,
    };

    const res = await fetch(`${ORIGIN}/wms/documents/update-big-ata-lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToSend),
    });

    if (!res.ok) throw new Error('Failed to update Big ATA lines in Salesforce');
    return res.json();
};

export const addOverridesToFormData = (formOverrides, documentData) => {
    const updatedDocumentData = {};

    // Go through each document
    for (const documentName in formOverrides) {
        if (!(documentName in documentData)) continue;

        const overrideDocument = _.clone(formOverrides[documentName]);
        const documentToUpdate = _.clone(documentData[documentName]);
        const defaults = _.clone(overrideDefaults[documentName]);

        for (const document of documentToUpdate) {
            // Go through each key to check for updates
            for (const override in overrideDocument) {
                // Skip overriding all data for pack sheets since there is more than one line
                const isPackSheet =
                    documentName === documentKeys.PACKING_SLIP_PRICED || documentName === documentKeys.PACKING_SLIP_UNPRICED;
                if (isPackSheet) {
                    if (!(override in defaults)) continue;

                    if (document[override] !== overrideDocument[override]) {
                        document[override] = overrideDocument[override];
                    }
                    continue;
                }

                if (document[override] !== overrideDocument[override]) {
                    document[override] = overrideDocument[override];
                }
            }
        }

        updatedDocumentData[documentName] = _.clone(documentToUpdate);
    }

    return updatedDocumentData;
};

export const isInputValid = (input, verificationType) => {
    if (verificationType === inputVerificationTypes.NUMBERS_ONLY) {
        return hasOnlyNumbers(input);
    }

    if (verificationType === inputVerificationTypes.TEXT_ONLY) {
        return hasOnlyText(input);
    }
};

export const hasOnlyNumbers = (value) => {
    if (value === '') return true;
    return /^\d+$/.test(value);
};

export const hasOnlyText = (value) => {
    if (value === '') return true;
    return /^[A-Za-z]+$/.test(value);
};

export const getInfoForBigATAExcel = async (releaseLines) => {
    const rlsNumber = releaseLines[0].releaseNumber;

    const normalizeValue = (val) => (val === 'N/A' ? '' : val);

    let hasAtLeastOneSerialNumber = false;
    let hasAtLeastOneTagAgency = false;
    const releaseNumberData = releaseLines.map((line) => {
        const serial = normalizeValue(line.serialNumber);
        const tagAgency = normalizeValue(line.tagAgency);
        if (line.serialNumber && line.serialNumber !== '' && line.serialNumber !== ' ') hasAtLeastOneSerialNumber = true;
        if (line.tagAgency && line.tagAgency !== '' && line.tagAgency !== ' ') hasAtLeastOneTagAgency = true;

        return {
            inventoryLineId: line.invLineId,
            PartNumber: line.partName,
            Keyword: line.keyword,
            ConditionCode: line.conditionCodeName,
            Quantity: line.quantity,
            Trace: line.trace,
            TagAgency: tagAgency,
            SerialNumber: serial,
        };
    });

    return {
        releaseLines,
        rlsNumber,
        releaseNumberData,
        hasAtLeastOneTagAgency,
        hasAtLeastOneSerialNumber,
    };
};

export const hasValue = (val) => {
    const s = (val ?? '').toString().trim();
    return s !== '' && s.toUpperCase() !== 'N/A';
};

export const makeAccountNameToId = (accountOptions = []) => {
    const m = new Map();
    for (const a of accountOptions) {
        const name = String(a?.Name ?? '').trim();
        const id = String(a?.Id ?? '').trim();
        if (!name || !id) continue;
        m.set(name.toLowerCase(), id);
    }
    return m;
};

export const resolveAccountId = (row, keyName, keyId, nameToId) => {
    const explicitId = String(row?.[keyId] ?? '').trim();
    if (explicitId) return explicitId;

    const name = String(row?.[keyName] ?? '')
        .trim()
        .toLowerCase();
    if (!name) return '';
    return nameToId.get(name) ?? '';
};

export const makeCondCodeNameToId = (codes = []) => {
    const m = new Map();
    for (const c of codes ?? []) {
        const name = String(c?.Name ?? '')
            .trim()
            .toLowerCase();
        const id = String(c?.Id ?? '').trim();
        if (name && id) m.set(name, id);
    }
    return m;
};

export const resolveCondCodeId = (row, ccNameToId) => {
    const explicit = String(row?.ConditionCodeId ?? '').trim();
    if (explicit) return explicit;

    const name = String(row?.ConditionCode ?? '')
        .trim()
        .toLowerCase();
    if (!name) return '';
    return ccNameToId.get(name) ?? '';
};

export const resolveAccountIdsForBigAtaRows = (rows = [], accountOptions = []) => {
    const nameToId = new Map(
        (accountOptions ?? []).filter((a) => a?.Name && a?.Id).map((a) => [String(a.Name).trim().toLowerCase(), a.Id])
    );

    return rows.map((r) => {
        const next = { ...(r ?? {}) };

        if (!next.TraceId) {
            const traceName = String(next.Trace ?? '')
                .trim()
                .toLowerCase();
            if (traceName) next.TraceId = nameToId.get(traceName) ?? next.TraceId;
        }

        if (!next.TagAgencyId) {
            const tagName = String(next.TagAgency ?? '')
                .trim()
                .toLowerCase();
            if (tagName) next.TagAgencyId = nameToId.get(tagName) ?? next.TagAgencyId;
        }

        return next;
    });
};

export const buildBigAtaSfPatches = (
    originalRows = [],
    updatedRows = [],
    accountOptions = [],
    bigAtaConditionCodes = []
) => {
    const nameToId = makeAccountNameToId(accountOptions ?? []);
    const patches = [];

    for (let i = 0; i < updatedRows.length; i++) {
        const before = originalRows[i] ?? {};
        const after = updatedRows[i] ?? {};

        const patch = {
            inventoryLineId: after.inventoryLineId,
        };

        // --- ConditionCode ---
        const ccNameToId = makeCondCodeNameToId(bigAtaConditionCodes ?? []);

        const beforeCCId = resolveCondCodeId(before, ccNameToId);
        const afterCCId = resolveCondCodeId(after, ccNameToId);

        if (beforeCCId !== afterCCId) {
            patch.ConditionCodeId = afterCCId || null;
        }

        // --- SerialNumber ---
        if ((before.SerialNumber ?? '') !== (after.SerialNumber ?? '')) {
            patch.SerialNumber = after.SerialNumber ?? '';
        }

        // --- Trace ---
        const beforeTraceId = resolveAccountId(before, 'Trace', 'TraceId', nameToId);
        const afterTraceId = resolveAccountId(after, 'Trace', 'TraceId', nameToId);

        const afterTraceHasText = hasValue(after.Trace);

        if (!afterTraceHasText && beforeTraceId) {
            patch.TraceId = null;
        } else if (afterTraceId && afterTraceId !== beforeTraceId) {
            patch.TraceId = afterTraceId;
        }

        // --- TagAgency ---
        const beforeTagId = resolveAccountId(before, 'TagAgency', 'TagAgencyId', nameToId);
        const afterTagId = resolveAccountId(after, 'TagAgency', 'TagAgencyId', nameToId);

        const afterTagHasText = hasValue(after.TagAgency);

        if (!afterTagHasText && beforeTagId) {
            patch.TagAgencyId = null;
        } else if (afterTagId && afterTagId !== beforeTagId) {
            patch.TagAgencyId = afterTagId;
        }

        const changedKeys = Object.keys(patch).filter((k) => k !== 'inventoryLineId');
        if (changedKeys.length) patches.push(patch);
    }

    return patches;
};

export const appendBigAtaRemarks = (matching) => {
    const existing = (matching?.remarks).toString().trim();

    const customerPo = (matching?.customerPoNumber).toString().trim();
    const customerPONumber = customerPo.split(' ')[1];
    const sellerRef = (matching?.releaseNumber).toString().trim();

    const extra = `CUSTOMER REFERENCE PO: ${customerPONumber} / SELLER REFERENCE: ${sellerRef}`;

    if (!existing) return extra;
    if (existing.includes(extra)) return existing;
    return `${existing}\n${extra}`;
};

export const buildCustomAsaForBigATA = ({ documentInfo, currentReleaseLine }) => {
    const asaKey = documentKeys.DOCUMENT_ASA;

    const allAsaRows = documentInfo?.documentData?.[asaKey] ?? [];
    const invName = currentReleaseLine?.invLineName;

    const matching = allAsaRows.find((row) => row.inventoryName === invName) ?? {};

    if (!matching || !matching.inventoryName) {
        throw new Error('No ASA data found for this inventory line.');
    }

    const ataText = 'SEE ATTACHED LIST';

    return {
        ...matching,

        // boxes 7-11
        partDescription: ataText,
        partNumber: ataText,
        appCode: ataText,
        partQuantity: ataText,
        serialNumber: ataText,

        // box 12:
        conditionCodeDescription: ataText,

        // box 13A:
        remarks: appendBigAtaRemarks(matching),

        // boxes 13B / 13C
        obtainedFrom: ataText,
        lastCertifiedAgency: ataText,
    };
};
